<script lang="ts" setup>
import type { Component } from 'vue'

import AbstractButton from '../abstract-button/AbstractButton.vue'
import Icon from '../icon/Icon.vue'
import type { TIconName } from '../icon/types'
import { IconButtonSize, IconButtonVariant } from './helpers'
import type { TIconButtonSize, TIconButtonVariant } from './types'

withDefaults(
  defineProps<{
    /**
     * The HTML tagname to use when rendering the component. Defaults to `button`.
     */
    // Note: this shadows the `AbstractButton` prop. Required for Options API
    as?: keyof HTMLElementTagNameMap | Component
    /**
     * The theme variant to use for styling the button
     */
    // Note: this shadows the `AbstractButton` prop. Required for Options API
    variant?: TIconButtonVariant
    /**
     * Indicates whether the user should not be able to interact with the input.
     */
    // Note: this shadows the `AbstractButton` prop. Required for Options API
    disabled?: boolean
    /**
     * Optional icon to show alongside the text content. The icon can be
     * positioned using the `icon-position` property.
     */
    icon: TIconName
    /**
     * Controls the size of the button
     */
    size?: TIconButtonSize
    /**
     * A text string that is used as the button's label
     */
    label: string
  }>(),
  {
    size: IconButtonSize.Medium,
    as: 'button',
    variant: IconButtonVariant.Primary,
    disabled: false,
  }
)
</script>

<template>
  <AbstractButton
    v-bind="$attrs"
    :as
    :variant
    :disabled
    class="glp-icon-button"
    :class="{
      'glp-icon-button--sm': size === IconButtonSize.Small,
      'glp-icon-button--lg': size === IconButtonSize.Large,
      'glp-icon-button--xl': size === IconButtonSize.ExtraLarge,
    }"
  >
    <Icon :name="icon" />
  </AbstractButton>
</template>

<style>
.glp-icon-button {
  --glp-button-padding: var(--glp-button-icon-md-padding);
  --glp-button-size-icon: var(--glp-button-icon-md-size-icon);
  padding: var(--glp-button-padding, 12px);
}

.glp-icon-button .glp-icon {
  display: block;
  --glp-icon-size: var(--glp-button-size-icon, 16px);
}

/** Sizes */
.glp-icon-button--xl {
  --glp-button-padding: var(--glp-button-icon-xl-padding);
  --glp-button-size-icon: var(--glp-button-icon-xl-size-icon);
}

.glp-icon-button--lg {
  --glp-button-padding: var(--glp-button-icon-lg-padding);
  --glp-button-size-icon: var(--glp-button-icon-lg-size-icon);
}

.glp-icon-button--sm {
  --glp-button-padding: var(--glp-button-icon-sm-padding);
  --glp-button-size-icon: var(--glp-button-icon-sm-size-icon);
}

/* Icon buttons have a different colour scheme if they are the tertiary 
 * variant. */
.glp-icon-button.glp-button--tertiary {
  /** background */
  --glp-button-tertiary-default-background: var(
    --glp-button-icon-tertiary-default-background
  );
  --glp-button-tertiary-pressed-background: var(
    --glp-button-icon-tertiary-pressed-background
  );
  --glp-button-tertiary-hover-background: var(
    --glp-button-icon-tertiary-hover-background
  );
  /* foreground */
  --glp-button-tertiary-default-foreground: var(
    --glp-button-icon-tertiary-default-foreground
  );
  --glp-button-tertiary-pressed-foreground: var(
    --glp-button-icon-tertiary-pressed-foreground
  );
  --glp-button-tertiary-hover-foreground: var(
    --glp-button-icon-tertiary-hover-foreground
  );
  /* border */
  --glp-button-tertiary-default-color-border: var(
    --glp-button-icon-tertiary-default-color-border
  );
  --glp-button-tertiary-pressed-color-border: var(
    --glp-button-icon-tertiary-pressed-color-border
  );
  --glp-button-tertiary-hover-color-border: var(
    --glp-button-icon-tertiary-hover-color-border
  );
}
</style>
